import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { ItemWithId, THandleAction, TTableStructure } from 'ui/EasTable/types';
import { TPageResponse } from 'redux/types';
import { PAGE_SIZE } from 'services/constants/env';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { useDispatch, useSelector } from 'react-redux';
import { CloudUpload } from '@mui/icons-material';
import { tableStructure, TEmAttachmentsRow } from './tableStructure';
import { AddAttachmentDialog } from './AddAttachmentDialog';
import {
	getEmAttachments,
	getEmAttachmentsLoading,
	getEmAttachmentDeleteLoading,
	getEmAttachmentDownloadLoading,
} from '../../../../../redux/reducers/attachments/selectors';
import {
	getEmAttachmentsRequest,
	deleteEmAttachmentRequest,
	downloadEmAttachmentRequest,
} from '../../../../../redux/reducers/attachments/reducer';
import { DeleteDialog } from '../../../../../ui/DeleteDialog';

export const EmAttachments = () => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const emAttachments = useSelector(getEmAttachments);
	const emAttachmentsLoading = useSelector(getEmAttachmentsLoading);
	const deleteLoading = useSelector(getEmAttachmentDeleteLoading);
	const downloadLoading = useSelector(getEmAttachmentDownloadLoading);

	const [deleteId, setDeleteId] = useState<number | null>(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openAddAttachmentDialog, setOpenAddAttachmentDialog] = useState(false);
	const setCloseAddAttachmentDialog = () => {
		setOpenAddAttachmentDialog(false);
	};
	useEffect(() => {
		if (emId) {
			dispatch(getEmAttachmentsRequest({ emId: Number(emId), pageNumber: 1, pageSize: PAGE_SIZE }));
		}
	}, [dispatch, emId]);

	const handleAction: THandleAction = useCallback(
		(id, action) => {
			if (!emId) return;
			const row = emAttachments?.data?.find(i => i.id === id);
			if (!row) return;

			if (action === 'Download') {
				if (!row.isDownloadAllowed) return;
				dispatch(
					downloadEmAttachmentRequest({
						emId: Number(emId),
						attachmentId: id,
						fileName: row.fileName,
					}),
				);
				return;
			}

			if (action === 'Delete') {
				if (!row.isDeleteAllowed) return;
				setDeleteId(id);
				setOpenDeleteDialog(true);
			}
		},
		[dispatch, emId, emAttachments],
	);

	const confirmDelete = useCallback(() => {
		if (!emId || !deleteId) return;
		dispatch(
			deleteEmAttachmentRequest({
				emId: Number(emId),
				attachmentId: deleteId,
				listRequest: { emId: Number(emId), pageNumber: 1, pageSize: PAGE_SIZE },
			}),
		);
		setOpenDeleteDialog(false);
		setDeleteId(null);
	}, [dispatch, deleteId, emId]);

	const itemsIsLoading = useMemo(
		() => emAttachmentsLoading || deleteLoading || downloadLoading,
		[emAttachmentsLoading, deleteLoading, downloadLoading],
	);

	return (
		<Box>
			{(() => {
				const attachmentsTableStructure = tableStructure as unknown as TTableStructure<
					keyof (TEmAttachmentsRow & ItemWithId),
					TEmAttachmentsRow
				>[];
				return (
					<EasTableWithoutFilter<TEmAttachmentsRow & ItemWithId>
						tableStructure={attachmentsTableStructure}
						items={emAttachments as TPageResponse<TEmAttachmentsRow> | null}
						itemsIsLoading={itemsIsLoading}
						handleAction={handleAction}
						skeletonRows={[1]}
					>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography variant="body1" color="primary" fontWeight="bold">
								Attachments
							</Typography>
							{(emAttachmentsLoading || deleteLoading || downloadLoading) && (
								<CircularProgress size={20} />
							)}
							<Button
								variant="contained"
								color="primary"
								size="small"
								onClick={() => {
									setOpenAddAttachmentDialog(true);
								}}
								startIcon={<CloudUpload sx={{ m: 0, p: 0 }} />}
							>
								Upload Attachment
							</Button>
						</Box>
					</EasTableWithoutFilter>
				);
			})()}
			<DeleteDialog
				open={openDeleteDialog}
				setClose={() => setOpenDeleteDialog(false)}
				handleDelete={confirmDelete}
				text="Are you sure you want to delete attachment?"
			/>
			<AddAttachmentDialog open={openAddAttachmentDialog} setClose={setCloseAddAttachmentDialog} />
		</Box>
	);
};
